import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Briefcase,
  Building2,
  FileText,
  Library,
  ClipboardCheck,
  ListChecks,
  Mail,
  Menu,
  Settings as SettingsIcon,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { SessionUser } from "@resume-cloudflare/shared";
import { cn } from "../lib/cn";
import UserChip from "./UserChip";
import { Toaster } from "./ui/Toaster";
import { TooltipProvider } from "./ui/Tooltip";

type NavItem = {
  to: string;
  label: string;
  Icon: LucideIcon;
  end?: boolean;
  adminOnly?: boolean;
};

type NavGroup = { title: string; items: NavItem[]; trailingNote?: string };

const NAV: NavGroup[] = [
  {
    title: "Job hunt",
    items: [
      { to: "/jobs", label: "Jobs", Icon: Briefcase },
      { to: "/companies", label: "Companies", Icon: Building2 },
      { to: "/review", label: "Review", Icon: ClipboardCheck },
      { to: "/applications", label: "Applications", Icon: ListChecks },
    ],
  },
  {
    title: "Insights",
    items: [{ to: "/insights", label: "Insights", Icon: BarChart3 }],
  },
  {
    title: "Outreach",
    items: [
      { to: "/contacts", label: "Contacts", Icon: Users },
      { to: "/templates", label: "Templates", Icon: SettingsIcon },
    ],
  },
  {
    title: "Materials",
    items: [
      { to: "/resumes", label: "Resumes", Icon: FileText },
      { to: "/corpus", label: "Career corpus", Icon: Library },
      { to: "/cover-letters", label: "Cover letters", Icon: Mail },
    ],
  },
  {
    title: "Operations",
    items: [
      { to: "/runs", label: "Runs", Icon: Activity },
      { to: "/dlq", label: "DLQ", Icon: AlertTriangle, adminOnly: true },
    ],
  },
];

const PAGE_TITLES: Record<string, string> = {
  "/": "Home",
  "/jobs": "Jobs",
  "/companies": "Companies",
  "/applications": "Applications",
  "/resumes": "Resumes",
  "/corpus": "Career corpus",
  "/cover-letters": "Cover letters",
  "/contacts": "Outreach",
  "/templates": "Message templates",
  "/insights": "Insights",
  "/runs": "Runs",
  "/dlq": "DLQ",
  "/settings": "Settings",
  "/profile": "Profile",
  "/profile/location": "Location preferences",
};

function pageTitleFor(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname]!;
  // Resume tailor workshop — both deep-link shapes share the same title.
  if (
    /^\/applications\/[^/]+\/resume$/.test(pathname) ||
    /^\/jobs\/[^/]+\/[^/]+\/tailor$/.test(pathname)
  ) {
    return "Resume tailor";
  }
  if (/^\/applications\/[^/]+\/apply$/.test(pathname)) {
    return "Application assistant";
  }
  for (const [path, title] of Object.entries(PAGE_TITLES)) {
    if (path !== "/" && pathname.startsWith(path)) return title;
  }
  return "Home";
}

export default function AppShell({
  user,
  children,
}: {
  user: SessionUser;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const title = pageTitleFor(location.pathname);

  return (
    <TooltipProvider>
      <div className="min-h-dvh bg-neutral-950 text-neutral-100">
        {/* Mobile drawer overlay + sidebar */}
        {mobileOpen ? (
          <div
            className="fixed inset-0 z-40 bg-black/60 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-neutral-800 bg-neutral-900 transition-transform lg:translate-x-0",
            mobileOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex items-center justify-between gap-2 border-b border-neutral-800 px-4 py-3">
            <NavLink
              to="/"
              className="text-sm font-semibold tracking-tight text-neutral-100"
              onClick={() => setMobileOpen(false)}
            >
              NeuralJobs
            </NavLink>
            <button
              type="button"
              aria-label="Close navigation"
              className="rounded-md p-1 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100 lg:hidden"
              onClick={() => setMobileOpen(false)}
            >
              <X size={16} />
            </button>
          </div>
          <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
            {NAV.map((group) => {
              const items = group.items.filter(
                (i) => !i.adminOnly || user.is_admin,
              );
              if (items.length === 0) return null;
              return (
                <div key={group.title} className="mb-5">
                  <p className="px-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-neutral-500">
                    {group.title}
                  </p>
                  <ul className="space-y-0.5">
                    {items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              "group flex items-center gap-2.5 rounded-md py-1.5 pr-2 pl-3 text-sm outline-none",
                              "border-l-2 border-transparent",
                              isActive
                                ? "border-l-accent-500 bg-neutral-800/80 text-neutral-50"
                                : "text-neutral-300 hover:bg-neutral-800/60 hover:text-neutral-100",
                            )
                          }
                        >
                          <item.Icon
                            size={15}
                            aria-hidden
                            className="shrink-0 text-neutral-500 group-hover:text-neutral-300"
                          />
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main column */}
        <div className="lg:pl-60">
          <header className="sticky top-0 z-30 flex h-12 items-center justify-between gap-3 border-b border-neutral-800 bg-neutral-950/80 px-4 backdrop-blur">
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Open navigation"
                className="rounded-md p-1.5 text-neutral-400 hover:bg-neutral-900 hover:text-neutral-100 lg:hidden"
                onClick={() => setMobileOpen(true)}
              >
                <Menu size={16} />
              </button>
              <h1 className="text-sm font-medium text-neutral-200">{title}</h1>
            </div>
            <UserChip user={user} />
          </header>

          <main>{children}</main>
        </div>

        <Toaster />
      </div>
    </TooltipProvider>
  );
}
